import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Sprout, LogOut, CalendarPlus, ClipboardList, Clock, Tag as TagIcon,
  User, Phone, MapPin, Mail, CheckCircle2, Lock, Ban, Sun, Trash2, Repeat,
  CalendarCheck, Plug, Pencil, PlusCircle, Save, X, Wrench, Home,
  Snowflake, Palette, Megaphone, BarChart3,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { JobLog } from "@/components/JobLog";

const RED_DEER = [52.2681, -113.8112];

function SnowMap({ bookings }) {
  const containerRef = useRef(null);
  const snow = bookings.filter(
    (b) => b.no_appointment && b.lat != null && b.lon != null && b.status !== "cancelled"
  );

  useEffect(() => {
    const L = window.L;
    if (!L || !containerRef.current) return;
    const map = L.map(containerRef.current).setView(RED_DEER, 12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);

    const pin = L.divIcon({
      className: "",
      html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;background:hsl(var(--primary));transform:rotate(-45deg);border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`,
      iconSize: [22, 22],
      iconAnchor: [11, 22],
      popupAnchor: [0, -20],
    });

    const esc = (s) => String(s || "").replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c]));
    const markers = snow.map((b) => {
      const m = L.marker([b.lat, b.lon], { icon: pin }).addTo(map);
      m.bindPopup(
        `<strong>${esc(b.customer.name)}</strong><br/>${esc(b.customer.address)}<br/>${esc(b.customer.phone)}<br/><em>${esc(b.service_name)}</em>`
      );
      return m;
    });
    if (markers.length) {
      const group = L.featureGroup(markers);
      map.fitBounds(group.getBounds().pad(0.3));
    }
    setTimeout(() => map.invalidateSize(), 150);
    return () => map.remove();
  }, [bookings]); // eslint-disable-line

  return (
    <>
      <div ref={containerRef} data-testid="snow-map" style={{ height: 480 }} className="rounded border border-border overflow-hidden z-0" />
      {snow.length === 0 && (
        <p className="text-muted-foreground text-sm mt-4" data-testid="no-snow-jobs">
          No snow-removal jobs yet. When someone requests one, their house appears as a pin here.
        </p>
      )}
    </>
  );
}


const TIME_OPTIONS = [
  "7:00 AM", "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM",
];

const STATUS_STYLES = {
  pending: "bg-accent/20 text-accent-foreground border-accent/40",
  confirmed: "bg-primary/10 text-primary border-primary/30",
  completed: "bg-secondary text-secondary-foreground border-border",
  cancelled: "bg-destructive/10 text-destructive border-destructive/30",
};
const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

const WEEKDAYS = [
  { i: 0, l: "Sun" }, { i: 1, l: "Mon" }, { i: 2, l: "Tue" }, { i: 3, l: "Wed" },
  { i: 4, l: "Thu" }, { i: 5, l: "Fri" }, { i: 6, l: "Sat" },
];

const toKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function prettyDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
    weekday: "long", month: "short", day: "numeric",
  });
}

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [slots, setSlots] = useState([]);
  const [blockedDays, setBlockedDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recurDays, setRecurDays] = useState([]);
  const [recurTimes, setRecurTimes] = useState([]);
  const [recurSaving, setRecurSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null); // service id being edited
  const [editForm, setEditForm] = useState({});
  const [newProduct, setNewProduct] = useState({ name: "", description: "", price: "", unit: "per visit" });
  const [calStatus, setCalStatus] = useState({ configured: false, connected: false });
  const [theme, setTheme] = useState("default");
  const [announcement, setAnnouncement] = useState({ active: false, title: "", text: "" });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => {
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/slots").then((r) => setSlots(r.data)).catch(() => {});
    api.get("/admin/blocked-days").then((r) => setBlockedDays(r.data)).catch(() => {});
    api.get("/admin/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/oauth/calendar/status").then((r) => setCalStatus(r.data)).catch(() => {});
    api.get("/theme").then((r) => setTheme(r.data.theme || "default")).catch(() => {});
    api.get("/announcement").then((r) => setAnnouncement(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (admin === false) navigate("/admin");
    if (admin) load();
  }, [admin]); // eslint-disable-line

  useEffect(() => {
    const c = searchParams.get("calendar");
    if (c === "connected") { toast.success("Google Calendar connected!"); setTab("availability"); }
    else if (c === "error") toast.error("Could not connect Google Calendar. Try again.");
    if (c) { searchParams.delete("calendar"); setSearchParams(searchParams, { replace: true }); }
  }, []); // eslint-disable-line

  // ---- Services (product) management ----
  const startEdit = (s) => { setEditing(s.id); setEditForm({ name: s.name, description: s.description, price: s.price ?? "", unit: s.unit, active: s.active, sale_active: !!s.sale_active, sale_type: s.sale_type || "percent", sale_value: s.sale_value ?? "", no_appointment: !!s.no_appointment }); };
  const saveEdit = async (id) => {
    try {
      await api.patch(`/admin/services/${id}`, {
        name: editForm.name, description: editForm.description,
        price: editForm.price === "" ? null : Number(editForm.price),
        unit: editForm.unit, active: editForm.active,
        sale_active: editForm.sale_active,
        sale_type: editForm.sale_type,
        sale_value: editForm.sale_value === "" ? 0 : Number(editForm.sale_value),
        no_appointment: editForm.no_appointment,
      });
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const saveTheme = async (t) => {
    try {
      await api.post("/admin/theme", { theme: t });
      setTheme(t);
      document.documentElement.setAttribute("data-theme", t);
      toast.success(`Theme set to ${t === "default" ? "Default (green)" : t}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const saveAnnouncement = async () => {
    setSettingsSaving(true);
    try {
      const res = await api.post("/admin/announcement", announcement);
      setAnnouncement(res.data);
      toast.success("Announcement saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSettingsSaving(false); }
  };
  const sendTestEmail = async () => {
    try {
      const res = await api.post("/admin/test-email");
      if (res.data.ok) toast.success(`Test email sent to ${res.data.to}`);
      else toast.error(res.data.error || "Email failed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const toggleActive = async (s) => {
    try { await api.patch(`/admin/services/${s.id}`, { active: !s.active }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const addProduct = async () => {
    if (!newProduct.name.trim()) { toast.error("Give the product a name."); return; }
    try {
      await api.post("/admin/services", { name: newProduct.name, description: newProduct.description, price: newProduct.price === "" ? null : Number(newProduct.price), unit: newProduct.unit });
      toast.success("Product added");
      setNewProduct({ name: "", description: "", price: "", unit: "per visit" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const deleteProduct = async (id) => {
    try { await api.delete(`/admin/services/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const connectCalendar = async () => {
    try {
      const res = await api.get("/oauth/calendar/login");
      window.location.href = res.data.authorization_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const disconnectCalendar = async () => {
    try { await api.post("/oauth/calendar/disconnect"); toast.success("Disconnected"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const slotsByDate = slots.reduce((acc, s) => {
    (acc[s.date] = acc[s.date] || []).push(s);
    return acc;
  }, {});
  const workingDates = Object.keys(slotsByDate).map((k) => new Date(k + "T00:00:00"));

  const selectedKey = selectedDay ? toKey(selectedDay) : null;
  const daySlots = selectedKey ? slotsByDate[selectedKey] || [] : [];
  const blockedDates = blockedDays.map((k) => new Date(k + "T00:00:00"));
  const isDayOff = selectedKey ? blockedDays.includes(selectedKey) : false;

  const toggleDayOff = async () => {
    if (!selectedKey || saving) return;
    setSaving(true);
    try {
      if (isDayOff) {
        await api.delete(`/admin/blocked-days/${selectedKey}`);
        toast.success("Day is available again");
      } else {
        await api.post("/admin/blocked-days", { date: selectedKey });
        toast.success("Day marked off — customers can't book it");
      }
      await Promise.resolve(load());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const clearDay = async () => {
    if (!selectedKey || saving) return;
    setSaving(true);
    try {
      const res = await api.post("/admin/slots/clear", { date: selectedKey });
      toast.success(`Cleared ${res.data.cleared} open slot${res.data.cleared !== 1 ? "s" : ""}`);
      await Promise.resolve(load());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const applyRecurring = async () => {
    if (recurDays.length === 0 || recurTimes.length === 0) {
      toast.error("Pick at least one weekday and one time.");
      return;
    }
    setRecurSaving(true);
    try {
      const res = await api.post("/admin/slots/recurring", { weekdays: recurDays, times: recurTimes, weeks: 4 });
      toast.success(`Added ${res.data.created} slots over the next 4 weeks`);
      setRecurDays([]);
      setRecurTimes([]);
      await Promise.resolve(load());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRecurSaving(false);
    }
  };

  const toggleTime = async (t) => {
    if (!selectedKey || saving) return;
    const existing = daySlots.find((s) => s.time === t);
    setSaving(true);
    try {
      if (existing) {
        if (existing.is_booked) {
          toast.error("That slot is already booked — cancel the booking first.");
          setSaving(false);
          return;
        }
        await api.delete(`/admin/slots/${existing.id}`);
      } else {
        await api.post("/admin/slots", { date: selectedKey, times: [t] });
      }
      await Promise.resolve(load());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (!admin) return <div className="min-h-screen bg-background" />;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-black/5">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center w-9 h-9 rounded bg-primary text-primary-foreground"><Sprout className="w-5 h-5" /></span>
            <span className="font-display font-extrabold tracking-tight text-lg">Owner Dashboard</span>
          </div>
          <button data-testid="admin-logout-button" onClick={() => { logout(); navigate("/admin"); }} className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-destructive transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex gap-2 mb-8">
          {[
            { k: "bookings", label: "Bookings", icon: ClipboardList },
            { k: "availability", label: "Availability", icon: CalendarPlus },
            { k: "services", label: "Services", icon: TagIcon },
            { k: "joblog", label: "Job Log", icon: BarChart3 },
            { k: "snow", label: "Snow Map", icon: Snowflake },
            { k: "settings", label: "Settings", icon: Palette },
          ].map((t) => (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded font-bold text-sm transition-colors ${tab === t.k ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-secondary/70"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <div data-testid="bookings-panel">
            <div className="flex justify-end mb-4">
              <button data-testid="send-test-email-button" onClick={sendTestEmail} className="inline-flex items-center gap-2 text-sm font-bold px-4 py-2 rounded border border-border hover:border-primary hover:text-primary transition-colors">
                <Mail className="w-4 h-4" /> Send test email
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {STATUSES.map((st) => (
                <div key={st} className="bg-card border border-border rounded p-5">
                  <div className="text-3xl font-display font-black text-primary">{bookings.filter((b) => b.status === st).length}</div>
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mt-1 capitalize">{st}</div>
                </div>
              ))}
            </div>

            {bookings.length === 0 ? (
              <div className="bg-card border border-border rounded p-12 text-center text-muted-foreground" data-testid="no-bookings">No bookings yet.</div>
            ) : (
              <div className="space-y-4">
                {bookings.map((b) => (
                  <div key={b.id} data-testid={`booking-row-${b.id}`} className="bg-card border border-border rounded p-6 grid md:grid-cols-12 gap-4 items-start">
                    <div className="md:col-span-3">
                      <div className="font-display font-bold text-lg">{b.service_name}</div>
                      {b.addon_names?.length > 0 && (
                        <div className="text-xs text-primary font-semibold mt-0.5">+ {b.addon_names.join(", ")}</div>
                      )}
                      {b.duration_minutes && (
                        <div className="text-xs text-muted-foreground mt-0.5">{b.duration_minutes} min{b.workers ? ` · ${b.workers} ${b.workers === 1 ? "person" : "people"}` : ""}</div>
                      )}
                      {(b.mower || b.whacker) && (
                        <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><Wrench className="w-3 h-3" />
                          {b.mower && (b.mower === "need" ? "Bring mower" : "Has mower")}
                          {b.mower && b.whacker ? " · " : ""}
                          {b.whacker && (b.whacker === "need" ? "Bring whacker" : "Has whacker")}
                        </div>
                      )}
                      <div className="text-sm text-primary font-semibold mt-1 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {prettyDate(b.date)} · {b.time}</div>
                      <div className="font-display font-bold text-primary mt-1">{b.total_price != null ? `$${b.total_price}` : "Custom quote"}</div>
                      {b.email_status?.customer && b.email_status.customer !== "sent" && (
                        <div className="text-xs text-destructive mt-1 flex items-start gap-1" data-testid={`email-warning-${b.id}`}><Mail className="w-3 h-3 mt-0.5 shrink-0" /> Customer email didn't send</div>
                      )}
                      {b.special_request && <p className="text-xs text-muted-foreground mt-2 italic">"{b.special_request}"</p>}
                      {b.notes && <p className="text-xs text-muted-foreground mt-2 italic" data-testid={`booking-notes-${b.id}`}>Note: "{b.notes}"</p>}
                      {b.pre_visit && <p className="text-xs text-primary font-semibold mt-1 flex items-center gap-1" data-testid={`booking-previsit-${b.id}`}>{b.pre_visit === "call" ? <><Phone className="w-3 h-3" /> Wants a call first</> : <><Home className="w-3 h-3" /> Wants a pre-visit</>}</p>}
                    </div>
                    <div className="md:col-span-5 text-sm space-y-1.5">
                      <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-muted-foreground" /> {b.customer.name}</div>
                      <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-muted-foreground" /> {b.customer.email}</div>
                      <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-muted-foreground" /> {b.customer.phone}</div>
                      <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5" /> {b.customer.address}</div>
                    </div>
                    <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
                      <span className={`inline-block text-xs font-bold uppercase tracking-wider px-3 py-1 rounded border capitalize ${STATUS_STYLES[b.status]}`}>{b.status}</span>
                      <div className="flex flex-wrap gap-2 md:justify-end">
                        {STATUSES.filter((s) => s !== b.status).map((s) => (
                          <button key={s} data-testid={`set-status-${s}-${b.id}`} onClick={() => setStatus(b.id, s)} className="text-xs font-bold px-3 py-1.5 rounded border border-border hover:border-primary hover:text-primary transition-colors capitalize">{s}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "availability" && (
          <div data-testid="availability-panel" className="space-y-10">
            <div className="bg-card border border-border rounded p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="gcal-card">
              <div className="flex items-start gap-3">
                <span className={`grid place-items-center w-10 h-10 rounded ${calStatus.connected ? "bg-primary text-primary-foreground" : "bg-secondary text-primary"}`}><CalendarCheck className="w-5 h-5" /></span>
                <div>
                  <h3 className="font-display font-bold text-lg">Google Calendar</h3>
                  <p className="text-muted-foreground text-sm">
                    {!calStatus.configured ? "Not set up yet — add your Google credentials to enable." :
                      calStatus.connected ? "Connected — new bookings appear on your Google Calendar automatically." :
                        "Connect so every booking lands on your Google Calendar."}
                  </p>
                </div>
              </div>
              {calStatus.configured && (calStatus.connected ? (
                <button data-testid="gcal-disconnect" onClick={disconnectCalendar} className="inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded border border-border hover:border-destructive hover:text-destructive transition-colors shrink-0">
                  <X className="w-4 h-4" /> Disconnect
                </button>
              ) : (
                <button data-testid="gcal-connect" onClick={connectCalendar} className="inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm font-bold px-5 py-2.5 rounded transition-transform active:scale-95 hover:bg-primary/90 shrink-0">
                  <Plug className="w-4 h-4" /> Connect Google Calendar
                </button>
              ))}
            </div>
            <div className="grid md:grid-cols-12 gap-8">
            <div className="md:col-span-6">
              <h2 className="font-display font-bold text-xl mb-1">Your work calendar</h2>
              <p className="text-muted-foreground text-sm mb-4">Green dot = has hours. Grey strikethrough = day off. Click a day to manage it.</p>
              <div className="bg-card border border-border rounded p-3 inline-block" data-testid="work-calendar">
                <Calendar
                  mode="single"
                  selected={selectedDay}
                  onSelect={setSelectedDay}
                  disabled={{ before: today }}
                  modifiers={{ working: workingDates, dayOff: blockedDates }}
                  modifiersClassNames={{
                    working: "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-primary aria-selected:after:bg-primary-foreground font-bold text-primary",
                    dayOff: "line-through text-muted-foreground opacity-60 after:!bg-muted-foreground",
                  }}
                />
              </div>
              <div className="flex flex-wrap gap-4 mt-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-primary" /> Has hours</span>
                <span className="flex items-center gap-1.5"><Ban className="w-3.5 h-3.5" /> Day off</span>
              </div>
            </div>

            <div className="md:col-span-6">
              {!selectedDay ? (
                <div className="bg-card border border-border rounded p-12 text-center text-muted-foreground h-full grid place-items-center" data-testid="no-day-selected">
                  <div>
                    <CalendarPlus className="w-8 h-8 mx-auto mb-3" />
                    <p>Select a day on the calendar to manage its time slots.</p>
                  </div>
                </div>
              ) : (
                <div data-testid="day-editor">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="font-display font-bold text-xl">{prettyDate(selectedKey)}</h2>
                      <p className="text-muted-foreground text-sm">Tap the times you're available. Tap again to remove.</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        data-testid="clear-day-button"
                        onClick={clearDay}
                        disabled={saving || daySlots.every((s) => s.is_booked)}
                        className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded border border-border text-muted-foreground hover:text-destructive hover:border-destructive transition-colors disabled:opacity-40"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Clear day
                      </button>
                      <button
                        data-testid="day-off-toggle"
                        onClick={toggleDayOff}
                        disabled={saving}
                        className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded border transition-colors disabled:opacity-40 ${isDayOff ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary hover:text-primary"}`}
                      >
                        {isDayOff ? <><Sun className="w-3.5 h-3.5" /> Make available</> : <><Ban className="w-3.5 h-3.5" /> Mark day off</>}
                      </button>
                    </div>
                  </div>

                  {isDayOff && (
                    <div className="mt-4 flex items-center gap-2 rounded border border-accent/40 bg-accent/15 px-4 py-3 text-sm" data-testid="day-off-banner">
                      <Ban className="w-4 h-4 text-accent-foreground shrink-0" />
                      <span>This is a <strong>day off</strong>. Customers can't book it, even if hours are set below.</span>
                    </div>
                  )}

                  <div className={`grid grid-cols-3 gap-2 mt-4 ${isDayOff ? "opacity-50" : ""}`}>
                    {TIME_OPTIONS.map((t) => {
                      const existing = daySlots.find((s) => s.time === t);
                      const active = !!existing;
                      const booked = existing?.is_booked;
                      return (
                        <button
                          key={t}
                          data-testid={`day-time-${t.replace(/[: ]/g, "")}`}
                          onClick={() => toggleTime(t)}
                          disabled={saving}
                          className={`relative text-sm font-medium py-3 rounded border transition-colors disabled:opacity-60 ${
                            booked
                              ? "bg-secondary border-primary/30 text-primary cursor-not-allowed"
                              : active
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-card border-border hover:border-primary"
                          }`}
                        >
                          {t}
                          {booked && <Lock className="w-3 h-3 absolute top-1.5 right-1.5" />}
                        </button>
                      );
                    })}
                  </div>
                  {daySlots.some((s) => s.is_booked) && (
                    <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Locked times are already booked by a customer.</p>
                  )}
                  <div className="mt-4 flex items-center gap-2 text-sm text-primary font-semibold">
                    <CheckCircle2 className="w-4 h-4" /> {daySlots.filter((s) => !s.is_booked).length} open · {daySlots.filter((s) => s.is_booked).length} booked
                  </div>
                </div>
              )}
            </div>
            </div>

            <div className="border-t border-border pt-8" data-testid="recurring-panel">
              <div className="flex items-center gap-2 mb-1">
                <Repeat className="w-5 h-5 text-primary" />
                <h2 className="font-display font-bold text-xl">Repeat a weekly schedule</h2>
              </div>
              <p className="text-muted-foreground text-sm mb-5">Pick the weekdays and times you usually work — I'll fill the calendar for the next 4 weeks. Days you've marked off are skipped.</p>
              <div className="grid lg:grid-cols-2 gap-8 max-w-4xl">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Weekdays</label>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {WEEKDAYS.map((d) => (
                      <button
                        key={d.i}
                        data-testid={`recur-day-${d.i}`}
                        onClick={() => setRecurDays((p) => (p.includes(d.i) ? p.filter((x) => x !== d.i) : [...p, d.i]))}
                        className={`w-12 h-12 rounded-full text-sm font-bold transition-colors ${recurDays.includes(d.i) ? "bg-primary text-primary-foreground" : "bg-card border border-border hover:border-primary"}`}
                      >
                        {d.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Times</label>
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    {TIME_OPTIONS.map((t) => (
                      <button
                        key={t}
                        data-testid={`recur-time-${t.replace(/[: ]/g, "")}`}
                        onClick={() => setRecurTimes((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]))}
                        className={`text-xs font-medium py-2 rounded border transition-colors ${recurTimes.includes(t) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary"}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <button
                data-testid="apply-recurring-button"
                onClick={applyRecurring}
                disabled={recurSaving || recurDays.length === 0 || recurTimes.length === 0}
                className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold px-7 py-3 rounded transition-transform active:scale-95 hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Repeat className="w-4 h-4" /> {recurSaving ? "Applying..." : "Apply to next 4 weeks"}
              </button>
            </div>
          </div>
        )}

        {tab === "services" && (
          <div data-testid="services-panel" className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 space-y-4">
              <h2 className="font-display font-bold text-xl">Your services</h2>
              {services.map((s) => (
                <div key={s.id} data-testid={`service-item-${s.id}`} className={`bg-card border rounded p-5 ${s.active ? "border-border" : "border-dashed border-border opacity-70"}`}>
                  {editing === s.id ? (
                    <div className="space-y-3">
                      <Input data-testid={`edit-name-${s.id}`} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Name" />
                      <Textarea data-testid={`edit-desc-${s.id}`} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} placeholder="Description" className="min-h-20" />
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <label className="text-xs text-muted-foreground">Price ($) — blank = custom quote</label>
                          <Input data-testid={`edit-price-${s.id}`} type="number" value={editForm.price} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} placeholder="Price" />
                        </div>
                        <div className="flex-1">
                          <label className="text-xs text-muted-foreground">Unit</label>
                          <Input data-testid={`edit-unit-${s.id}`} value={editForm.unit} onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })} placeholder="per visit" />
                        </div>
                      </div>
                      <div className="rounded border border-border p-3 bg-background/50">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" data-testid={`edit-sale-active-${s.id}`} checked={editForm.sale_active} onChange={(e) => setEditForm({ ...editForm, sale_active: e.target.checked })} className="w-4 h-4 accent-[hsl(var(--primary))]" />
                          <span className="text-sm font-bold flex items-center gap-1.5"><TagIcon className="w-3.5 h-3.5" /> Put this on sale</span>
                        </label>
                        {editForm.sale_active && (
                          <div className="flex gap-3 mt-3">
                            <select data-testid={`edit-sale-type-${s.id}`} value={editForm.sale_type} onChange={(e) => setEditForm({ ...editForm, sale_type: e.target.value })} className="flex-1 h-10 rounded border border-border bg-background px-3 text-sm">
                              <option value="percent">% off</option>
                              <option value="amount">$ off</option>
                            </select>
                            <Input data-testid={`edit-sale-value-${s.id}`} type="number" value={editForm.sale_value} onChange={(e) => setEditForm({ ...editForm, sale_value: e.target.value })} placeholder={editForm.sale_type === "percent" ? "e.g. 20" : "e.g. 10"} className="flex-1" />
                          </div>
                        )}
                      </div>

                      <label className="flex items-center gap-2 cursor-pointer rounded border border-border p-3 bg-background/50">
                        <input type="checkbox" data-testid={`edit-no-appointment-${s.id}`} checked={!!editForm.no_appointment} onChange={(e) => setEditForm({ ...editForm, no_appointment: e.target.checked })} className="w-4 h-4 accent-[hsl(var(--primary))]" />
                        <span className="text-sm font-bold flex items-center gap-1.5"><Snowflake className="w-3.5 h-3.5" /> No appointment needed (skips date/time &amp; pins on the Snow Map)</span>
                      </label>

                      <div className="flex gap-2">
                        <button data-testid={`save-service-${s.id}`} onClick={() => saveEdit(s.id)} className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm font-bold px-4 py-2 rounded hover:bg-primary/90"><Save className="w-4 h-4" /> Save</button>
                        <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded border border-border hover:border-primary"><X className="w-4 h-4" /> Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-display font-bold text-lg">{s.name}</h3>
                          {s.on_sale && s.price != null && <span className="text-sm text-muted-foreground line-through">${s.price}</span>}
                          <span className="font-display font-bold text-primary">{s.price != null ? `$${s.on_sale ? s.sale_price : s.price}` : "Quote"}</span>
                          <span className="text-xs text-muted-foreground">{s.unit}</span>
                          {s.on_sale && <span data-testid={`sale-badge-${s.id}`} className="text-[10px] font-bold uppercase tracking-wider bg-accent text-accent-foreground px-2 py-0.5 rounded">{s.sale_label}</span>}
                          {!s.active && <span className="text-[10px] font-bold uppercase tracking-wider bg-secondary px-2 py-0.5 rounded">Hidden</span>}
                          {s.is_addon && <span className="text-[10px] font-bold uppercase tracking-wider bg-accent/20 text-accent-foreground px-2 py-0.5 rounded">Add-on ${s.on_sale ? s.addon_sale_price : s.addon_price}</span>}
                          {s.no_appointment && <span data-testid={`no-appt-badge-${s.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-primary/10 text-primary px-2 py-0.5 rounded"><Snowflake className="w-2.5 h-2.5" /> No appt</span>}
                        </div>
                        <p className="text-muted-foreground text-sm mt-1">{s.description}</p>
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        <button data-testid={`edit-service-${s.id}`} onClick={() => startEdit(s)} className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded border border-border hover:border-primary hover:text-primary transition-colors"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                        <button data-testid={`toggle-service-${s.id}`} onClick={() => toggleActive(s)} className="text-xs font-bold px-3 py-1.5 rounded border border-border hover:border-primary transition-colors">{s.active ? "Hide" : "Show"}</button>
                        {s.editable && <button data-testid={`delete-service-${s.id}`} onClick={() => deleteProduct(s.id)} className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded border border-border text-destructive hover:border-destructive transition-colors"><Trash2 className="w-3.5 h-3.5" /> Delete</button>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="lg:col-span-5">
              <div className="bg-card border border-border rounded p-6 sticky top-24" data-testid="new-product-form">
                <div className="flex items-center gap-2 mb-4"><PlusCircle className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-xl">Add a product</h2></div>
                <div className="space-y-3">
                  <Input data-testid="new-product-name" value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} placeholder="Service name (e.g. Leaf Removal)" />
                  <Textarea data-testid="new-product-desc" value={newProduct.description} onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })} placeholder="Short description" className="min-h-20" />
                  <div className="flex gap-3">
                    <Input data-testid="new-product-price" type="number" value={newProduct.price} onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })} placeholder="Price ($)" />
                    <Input data-testid="new-product-unit" value={newProduct.unit} onChange={(e) => setNewProduct({ ...newProduct, unit: e.target.value })} placeholder="per visit" />
                  </div>
                  <button data-testid="add-product-button" onClick={addProduct} className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-bold py-3 rounded transition-transform active:scale-95 hover:bg-primary/90"><PlusCircle className="w-4 h-4" /> Add product</button>
                  <p className="text-xs text-muted-foreground">Leave price blank for a custom-quote service.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "joblog" && <JobLog />}

        {tab === "snow" && (
          <div data-testid="snow-panel">
            <div className="flex items-center gap-2 mb-1">
              <Snowflake className="w-5 h-5 text-primary" />
              <h2 className="font-display font-bold text-xl">Snow removal map</h2>
            </div>
            <p className="text-muted-foreground text-sm mb-5">Every no-appointment job shows as a pin around Red Deer. Click a pin for the customer's details.</p>
            <SnowMap bookings={bookings} />
            {bookings.filter((b) => b.no_appointment && b.status !== "cancelled").length > 0 && (
              <div className="mt-6 space-y-3" data-testid="snow-list">
                <h3 className="font-display font-bold text-lg">Snow jobs</h3>
                {bookings.filter((b) => b.no_appointment && b.status !== "cancelled").map((b) => (
                  <div key={b.id} data-testid={`snow-job-${b.id}`} className="bg-card border border-border rounded p-4 flex flex-wrap items-start justify-between gap-3">
                    <div className="text-sm space-y-1">
                      <div className="font-display font-bold flex items-center gap-2">{b.service_name}
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border capitalize ${STATUS_STYLES[b.status]}`}>{b.status}</span>
                      </div>
                      <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-muted-foreground" /> {b.customer.name}</div>
                      <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-muted-foreground" /> {b.customer.phone}</div>
                      <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5" /> {b.customer.address}</div>
                      {b.lat == null && <div className="text-xs text-destructive flex items-center gap-1"><MapPin className="w-3 h-3" /> Couldn't map this address — check it manually</div>}
                    </div>
                    <div className="flex flex-wrap gap-2 justify-end">
                      {STATUSES.filter((s) => s !== b.status).map((s) => (
                        <button key={s} data-testid={`snow-set-status-${s}-${b.id}`} onClick={() => setStatus(b.id, s)} className="text-xs font-bold px-3 py-1.5 rounded border border-border hover:border-primary hover:text-primary transition-colors capitalize">{s}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "settings" && (
          <div data-testid="settings-panel" className="grid lg:grid-cols-2 gap-8 max-w-5xl">
            <div className="bg-card border border-border rounded p-6" data-testid="theme-card">
              <div className="flex items-center gap-2 mb-1"><Palette className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-xl">Site theme</h2></div>
              <p className="text-muted-foreground text-sm mb-5">Restyle the whole site for the season. Changes take effect instantly for visitors.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { k: "default", label: "Default", note: "Fresh green & white", sw: ["#2e7d4f", "#c79a6b", "#f7f8f2"] },
                  { k: "winter", label: "Winter", note: "Icy blues & white", sw: ["#2478a8", "#3fb2c4", "#eef5fa"] },
                  { k: "christmas", label: "Christmas", note: "Festive red & green", sw: ["#1f5130", "#c62828", "#fbf6f6"] },
                ].map((t) => (
                  <button key={t.k} data-testid={`theme-${t.k}`} onClick={() => saveTheme(t.k)}
                    className={`rounded border p-4 text-left transition-colors ${theme === t.k ? "border-primary ring-2 ring-primary/30 bg-secondary/40" : "border-border hover:border-primary"}`}>
                    <div className="flex gap-1.5 mb-3">
                      {t.sw.map((c, i) => <span key={i} className="w-5 h-5 rounded-full border border-black/10" style={{ background: c }} />)}
                    </div>
                    <div className="font-display font-bold flex items-center gap-1.5">{t.label}{theme === t.k && <CheckCircle2 className="w-4 h-4 text-primary" />}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{t.note}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border rounded p-6" data-testid="announcement-card">
              <div className="flex items-center gap-2 mb-1"><Megaphone className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-xl">Announcement bar</h2></div>
              <p className="text-muted-foreground text-sm mb-5">Show a message across the top of your landing page — sales, seasonal notes, or holiday hours.</p>
              <label className="flex items-center gap-2 cursor-pointer mb-4">
                <input type="checkbox" data-testid="announcement-active" checked={!!announcement.active} onChange={(e) => setAnnouncement({ ...announcement, active: e.target.checked })} className="w-4 h-4 accent-[hsl(var(--primary))]" />
                <span className="text-sm font-bold">Show the announcement bar</span>
              </label>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Title (optional)</label>
                  <Input data-testid="announcement-title" value={announcement.title} onChange={(e) => setAnnouncement({ ...announcement, title: e.target.value })} placeholder="Winter is here!" className="mt-1.5" maxLength={120} />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Message</label>
                  <Textarea data-testid="announcement-text" value={announcement.text} onChange={(e) => setAnnouncement({ ...announcement, text: e.target.value })} placeholder="Now offering snow removal — no appointment needed. Book anytime!" className="mt-1.5 min-h-20" maxLength={400} />
                </div>
                <button data-testid="save-announcement-button" onClick={saveAnnouncement} disabled={settingsSaving} className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold px-6 py-3 rounded transition-transform active:scale-95 hover:bg-primary/90 disabled:opacity-40">
                  <Save className="w-4 h-4" /> {settingsSaving ? "Saving..." : "Save announcement"}
                </button>
              </div>
              {(announcement.active && (announcement.title || announcement.text)) && (
                <div className="mt-5">
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">Preview</div>
                  <div className="bg-accent text-accent-foreground rounded px-4 py-2.5 flex items-center gap-3 text-sm" data-testid="announcement-preview">
                    <Megaphone className="w-4 h-4 shrink-0" />
                    <p className="flex-1">{announcement.title && <span className="font-bold">{announcement.title}</span>}{announcement.title && announcement.text ? " — " : ""}{announcement.text}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
